// Sports Wordle Player Data

export interface Player {
  id: string
  firstName: string
  lastName: string
  fullName: string
  sport: "basketball" | "soccer"
  league: string
  team: string
  position: string
  jerseyNumber?: number
  yearsActive: string // e.g., "2003-present" or "2000-2016"
  yearStart: number
  yearEnd: number | null // null for current players
  country?: string
  isRetired: boolean
  height?: string // e.g., "6'9""
  conference?: "East" | "West" // for NBA
}

// Sample NBA Players (mix of current and retired legends)
export const nbaPlayers: Player[] = [
  // Current Stars
  {
    id: "lebron",
    firstName: "LeBron",
    lastName: "James",
    fullName: "LeBron James",
    sport: "basketball",
    league: "NBA",
    team: "Lakers",
    position: "F",
    jerseyNumber: 23,
    yearsActive: "2003-present",
    yearStart: 2003,
    yearEnd: null,
    country: "USA",
    isRetired: false,
    height: "6'9\"",
    conference: "West",
  },
  {
    id: "curry",
    firstName: "Stephen",
    lastName: "Curry",
    fullName: "Stephen Curry",
    sport: "basketball",
    league: "NBA",
    team: "Warriors",
    position: "Guard",
    jerseyNumber: 30,
    yearsActive: "2009-present",
    yearStart: 2009,
    yearEnd: null,
    country: "USA",
    isRetired: false,
  },
  {
    id: "giannis",
    firstName: "Giannis",
    lastName: "Antetokounmpo",
    fullName: "Giannis Antetokounmpo",
    sport: "basketball",
    league: "NBA",
    team: "Bucks",
    position: "Forward",
    jerseyNumber: 34,
    yearsActive: "2013-present",
    yearStart: 2013,
    yearEnd: null,
    country: "Greece",
    isRetired: false,
  },
  {
    id: "durant",
    firstName: "Kevin",
    lastName: "Durant",
    fullName: "Kevin Durant",
    sport: "basketball",
    league: "NBA",
    team: "Suns",
    position: "Forward",
    jerseyNumber: 35,
    yearsActive: "2007-present",
    yearStart: 2007,
    yearEnd: null,
    country: "USA",
    isRetired: false,
  },
  {
    id: "jokic",
    firstName: "Nikola",
    lastName: "Jokic",
    fullName: "Nikola Jokic",
    sport: "basketball",
    league: "NBA",
    team: "Nuggets",
    position: "Center",
    jerseyNumber: 15,
    yearsActive: "2015-present",
    yearStart: 2015,
    yearEnd: null,
    country: "Serbia",
    isRetired: false,
  },
  {
    id: "luka",
    firstName: "Luka",
    lastName: "Doncic",
    fullName: "Luka Doncic",
    sport: "basketball",
    league: "NBA",
    team: "Mavericks",
    position: "Guard",
    jerseyNumber: 77,
    yearsActive: "2018-present",
    yearStart: 2018,
    yearEnd: null,
    country: "Slovenia",
    isRetired: false,
  },
  {
    id: "tatum",
    firstName: "Jayson",
    lastName: "Tatum",
    fullName: "Jayson Tatum",
    sport: "basketball",
    league: "NBA",
    team: "Celtics",
    position: "Forward",
    jerseyNumber: 0,
    yearsActive: "2017-present",
    yearStart: 2017,
    yearEnd: null,
    country: "USA",
    isRetired: false,
  },
  {
    id: "embiid",
    firstName: "Joel",
    lastName: "Embiid",
    fullName: "Joel Embiid",
    sport: "basketball",
    league: "NBA",
    team: "76ers",
    position: "Center",
    jerseyNumber: 21,
    yearsActive: "2016-present",
    yearStart: 2016,
    yearEnd: null,
    country: "Cameroon",
    isRetired: false,
  },
  {
    id: "lillard",
    firstName: "Damian",
    lastName: "Lillard",
    fullName: "Damian Lillard",
    sport: "basketball",
    league: "NBA",
    team: "Bucks",
    position: "Guard",
    jerseyNumber: 0,
    yearsActive: "2012-present",
    yearStart: 2012,
    yearEnd: null,
    country: "USA",
    isRetired: false,
  },
  {
    id: "butler",
    firstName: "Jimmy",
    lastName: "Butler",
    fullName: "Jimmy Butler",
    sport: "basketball",
    league: "NBA",
    team: "Heat",
    position: "Forward",
    jerseyNumber: 22,
    yearsActive: "2011-present",
    yearStart: 2011,
    yearEnd: null,
    country: "USA",
    isRetired: false,
  },

  // Retired Legends
  {
    id: "jordan",
    firstName: "Michael",
    lastName: "Jordan",
    fullName: "Michael Jordan",
    sport: "basketball",
    league: "NBA",
    team: "Bulls",
    position: "Guard",
    jerseyNumber: 23,
    yearsActive: "1984-2003",
    yearStart: 1984,
    yearEnd: 2003,
    country: "USA",
    isRetired: true,
  },
  {
    id: "kobe",
    firstName: "Kobe",
    lastName: "Bryant",
    fullName: "Kobe Bryant",
    sport: "basketball",
    league: "NBA",
    team: "Lakers",
    position: "Guard",
    jerseyNumber: 24,
    yearsActive: "1996-2016",
    yearStart: 1996,
    yearEnd: 2016,
    country: "USA",
    isRetired: true,
  },
  {
    id: "shaq",
    firstName: "Shaquille",
    lastName: "O'Neal",
    fullName: "Shaquille O'Neal",
    sport: "basketball",
    league: "NBA",
    team: "Lakers",
    position: "Center",
    jerseyNumber: 34,
    yearsActive: "1992-2011",
    yearStart: 1992,
    yearEnd: 2011,
    country: "USA",
    isRetired: true,
  },
  {
    id: "duncan",
    firstName: "Tim",
    lastName: "Duncan",
    fullName: "Tim Duncan",
    sport: "basketball",
    league: "NBA",
    team: "Spurs",
    position: "Forward",
    jerseyNumber: 21,
    yearsActive: "1997-2016",
    yearStart: 1997,
    yearEnd: 2016,
    country: "USA",
    isRetired: true,
  },
  {
    id: "magic",
    firstName: "Magic",
    lastName: "Johnson",
    fullName: "Magic Johnson",
    sport: "basketball",
    league: "NBA",
    team: "Lakers",
    position: "Guard",
    jerseyNumber: 32,
    yearsActive: "1979-1996",
    yearStart: 1979,
    yearEnd: 1996,
    country: "USA",
    isRetired: true,
  },
  {
    id: "bird",
    firstName: "Larry",
    lastName: "Bird",
    fullName: "Larry Bird",
    sport: "basketball",
    league: "NBA",
    team: "Celtics",
    position: "Forward",
    jerseyNumber: 33,
    yearsActive: "1979-1992",
    yearStart: 1979,
    yearEnd: 1992,
    country: "USA",
    isRetired: true,
  },
  {
    id: "kareem",
    firstName: "Kareem",
    lastName: "Abdul-Jabbar",
    fullName: "Kareem Abdul-Jabbar",
    sport: "basketball",
    league: "NBA",
    team: "Lakers",
    position: "Center",
    jerseyNumber: 33,
    yearsActive: "1969-1989",
    yearStart: 1969,
    yearEnd: 1989,
    country: "USA",
    isRetired: true,
  },
  {
    id: "hakeem",
    firstName: "Hakeem",
    lastName: "Olajuwon",
    fullName: "Hakeem Olajuwon",
    sport: "basketball",
    league: "NBA",
    team: "Rockets",
    position: "Center",
    jerseyNumber: 34,
    yearsActive: "1984-2002",
    yearStart: 1984,
    yearEnd: 2002,
    country: "Nigeria",
    isRetired: true,
  },
  {
    id: "dirk",
    firstName: "Dirk",
    lastName: "Nowitzki",
    fullName: "Dirk Nowitzki",
    sport: "basketball",
    league: "NBA",
    team: "Mavericks",
    position: "Forward",
    jerseyNumber: 41,
    yearsActive: "1998-2019",
    yearStart: 1998,
    yearEnd: 2019,
    country: "Germany",
    isRetired: true,
  },
  {
    id: "iverson",
    firstName: "Allen",
    lastName: "Iverson",
    fullName: "Allen Iverson",
    sport: "basketball",
    league: "NBA",
    team: "76ers",
    position: "Guard",
    jerseyNumber: 3,
    yearsActive: "1996-2010",
    yearStart: 1996,
    yearEnd: 2010,
    country: "USA",
    isRetired: true,
  },
]

// Sample Soccer Players from Top 5 Leagues
export const soccerPlayers: Player[] = [
  // Premier League
  {
    id: "haaland",
    firstName: "Erling",
    lastName: "Haaland",
    fullName: "Erling Haaland",
    sport: "soccer",
    league: "Premier League",
    team: "Manchester City",
    position: "Forward",
    jerseyNumber: 9,
    yearsActive: "2019-present",
    yearStart: 2019,
    yearEnd: null,
    country: "Norway",
    isRetired: false,
  },
  {
    id: "salah",
    firstName: "Mohamed",
    lastName: "Salah",
    fullName: "Mohamed Salah",
    sport: "soccer",
    league: "Premier League",
    team: "Liverpool",
    position: "Forward",
    jerseyNumber: 11,
    yearsActive: "2012-present",
    yearStart: 2012,
    yearEnd: null,
    country: "Egypt",
    isRetired: false,
  },
  {
    id: "kane",
    firstName: "Harry",
    lastName: "Kane",
    fullName: "Harry Kane",
    sport: "soccer",
    league: "Bundesliga",
    team: "Bayern Munich",
    position: "Forward",
    jerseyNumber: 9,
    yearsActive: "2009-present",
    yearStart: 2009,
    yearEnd: null,
    country: "England",
    isRetired: false,
  },
  {
    id: "debruyne",
    firstName: "Kevin",
    lastName: "De Bruyne",
    fullName: "Kevin De Bruyne",
    sport: "soccer",
    league: "Premier League",
    team: "Manchester City",
    position: "Midfielder",
    jerseyNumber: 17,
    yearsActive: "2008-present",
    yearStart: 2008,
    yearEnd: null,
    country: "Belgium",
    isRetired: false,
  },
  {
    id: "rashford",
    firstName: "Marcus",
    lastName: "Rashford",
    fullName: "Marcus Rashford",
    sport: "soccer",
    league: "Premier League",
    team: "Manchester United",
    position: "Forward",
    jerseyNumber: 10,
    yearsActive: "2015-present",
    yearStart: 2015,
    yearEnd: null,
    country: "England",
    isRetired: false,
  },

  // La Liga
  {
    id: "bellingham",
    firstName: "Jude",
    lastName: "Bellingham",
    fullName: "Jude Bellingham",
    sport: "soccer",
    league: "La Liga",
    team: "Real Madrid",
    position: "Midfielder",
    jerseyNumber: 5,
    yearsActive: "2019-present",
    yearStart: 2019,
    yearEnd: null,
    country: "England",
    isRetired: false,
  },
  {
    id: "vinicius",
    firstName: "Vinicius",
    lastName: "Junior",
    fullName: "Vinicius Junior",
    sport: "soccer",
    league: "La Liga",
    team: "Real Madrid",
    position: "Forward",
    jerseyNumber: 7,
    yearsActive: "2017-present",
    yearStart: 2017,
    yearEnd: null,
    country: "Brazil",
    isRetired: false,
  },
  {
    id: "lewandowski",
    firstName: "Robert",
    lastName: "Lewandowski",
    fullName: "Robert Lewandowski",
    sport: "soccer",
    league: "La Liga",
    team: "Barcelona",
    position: "Forward",
    jerseyNumber: 9,
    yearsActive: "2005-present",
    yearStart: 2005,
    yearEnd: null,
    country: "Poland",
    isRetired: false,
  },
  {
    id: "pedri",
    firstName: "Pedri",
    lastName: "González",
    fullName: "Pedri González",
    sport: "soccer",
    league: "La Liga",
    team: "Barcelona",
    position: "Midfielder",
    jerseyNumber: 8,
    yearsActive: "2019-present",
    yearStart: 2019,
    yearEnd: null,
    country: "Spain",
    isRetired: false,
  },
  {
    id: "modric",
    firstName: "Luka",
    lastName: "Modric",
    fullName: "Luka Modric",
    sport: "soccer",
    league: "La Liga",
    team: "Real Madrid",
    position: "Midfielder",
    jerseyNumber: 10,
    yearsActive: "2003-present",
    yearStart: 2003,
    yearEnd: null,
    country: "Croatia",
    isRetired: false,
  },

  // Serie A
  {
    id: "osimhen",
    firstName: "Victor",
    lastName: "Osimhen",
    fullName: "Victor Osimhen",
    sport: "soccer",
    league: "Serie A",
    team: "Napoli",
    position: "Forward",
    jerseyNumber: 9,
    yearsActive: "2015-present",
    yearStart: 2015,
    yearEnd: null,
    country: "Nigeria",
    isRetired: false,
  },
  {
    id: "lautaro",
    firstName: "Lautaro",
    lastName: "Martinez",
    fullName: "Lautaro Martinez",
    sport: "soccer",
    league: "Serie A",
    team: "Inter Milan",
    position: "Forward",
    jerseyNumber: 10,
    yearsActive: "2013-present",
    yearStart: 2013,
    yearEnd: null,
    country: "Argentina",
    isRetired: false,
  },
  {
    id: "vlahovic",
    firstName: "Dusan",
    lastName: "Vlahovic",
    fullName: "Dusan Vlahovic",
    sport: "soccer",
    league: "Serie A",
    team: "Juventus",
    position: "Forward",
    jerseyNumber: 9,
    yearsActive: "2015-present",
    yearStart: 2015,
    yearEnd: null,
    country: "Serbia",
    isRetired: false,
  },
  {
    id: "barella",
    firstName: "Nicolo",
    lastName: "Barella",
    fullName: "Nicolo Barella",
    sport: "soccer",
    league: "Serie A",
    team: "Inter Milan",
    position: "Midfielder",
    jerseyNumber: 23,
    yearsActive: "2015-present",
    yearStart: 2015,
    yearEnd: null,
    country: "Italy",
    isRetired: false,
  },
  {
    id: "chiesa",
    firstName: "Federico",
    lastName: "Chiesa",
    fullName: "Federico Chiesa",
    sport: "soccer",
    league: "Serie A",
    team: "Juventus",
    position: "Forward",
    jerseyNumber: 7,
    yearsActive: "2016-present",
    yearStart: 2016,
    yearEnd: null,
    country: "Italy",
    isRetired: false,
  },

  // Bundesliga
  {
    id: "musiala",
    firstName: "Jamal",
    lastName: "Musiala",
    fullName: "Jamal Musiala",
    sport: "soccer",
    league: "Bundesliga",
    team: "Bayern Munich",
    position: "Midfielder",
    jerseyNumber: 42,
    yearsActive: "2020-present",
    yearStart: 2020,
    yearEnd: null,
    country: "Germany",
    isRetired: false,
  },
  {
    id: "wirtz",
    firstName: "Florian",
    lastName: "Wirtz",
    fullName: "Florian Wirtz",
    sport: "soccer",
    league: "Bundesliga",
    team: "Bayer Leverkusen",
    position: "Midfielder",
    jerseyNumber: 10,
    yearsActive: "2020-present",
    yearStart: 2020,
    yearEnd: null,
    country: "Germany",
    isRetired: false,
  },
  {
    id: "sane",
    firstName: "Leroy",
    lastName: "Sane",
    fullName: "Leroy Sane",
    sport: "soccer",
    league: "Bundesliga",
    team: "Bayern Munich",
    position: "Forward",
    jerseyNumber: 10,
    yearsActive: "2014-present",
    yearStart: 2014,
    yearEnd: null,
    country: "Germany",
    isRetired: false,
  },

  // Ligue 1
  {
    id: "mbappe",
    firstName: "Kylian",
    lastName: "Mbappe",
    fullName: "Kylian Mbappe",
    sport: "soccer",
    league: "La Liga",
    team: "Real Madrid",
    position: "Forward",
    jerseyNumber: 9,
    yearsActive: "2015-present",
    yearStart: 2015,
    yearEnd: null,
    country: "France",
    isRetired: false,
  },
  {
    id: "dembele",
    firstName: "Ousmane",
    lastName: "Dembele",
    fullName: "Ousmane Dembele",
    sport: "soccer",
    league: "Ligue 1",
    team: "PSG",
    position: "Forward",
    jerseyNumber: 10,
    yearsActive: "2016-present",
    yearStart: 2016,
    yearEnd: null,
    country: "France",
    isRetired: false,
  },

  // Retired Legends
  {
    id: "messi",
    firstName: "Lionel",
    lastName: "Messi",
    fullName: "Lionel Messi",
    sport: "soccer",
    league: "MLS",
    team: "Inter Miami",
    position: "Forward",
    jerseyNumber: 10,
    yearsActive: "2004-present",
    yearStart: 2004,
    yearEnd: null,
    country: "Argentina",
    isRetired: false,
  },
  {
    id: "ronaldo",
    firstName: "Cristiano",
    lastName: "Ronaldo",
    fullName: "Cristiano Ronaldo",
    sport: "soccer",
    league: "Saudi Pro League",
    team: "Al Nassr",
    position: "Forward",
    jerseyNumber: 7,
    yearsActive: "2002-present",
    yearStart: 2002,
    yearEnd: null,
    country: "Portugal",
    isRetired: false,
  },
  {
    id: "pele",
    firstName: "Pele",
    lastName: "",
    fullName: "Pele",
    sport: "soccer",
    league: "Various",
    team: "Santos/Brazil",
    position: "Forward",
    jerseyNumber: 10,
    yearsActive: "1956-1977",
    yearStart: 1956,
    yearEnd: 1977,
    country: "Brazil",
    isRetired: true,
  },
  {
    id: "maradona",
    firstName: "Diego",
    lastName: "Maradona",
    fullName: "Diego Maradona",
    sport: "soccer",
    league: "Various",
    team: "Napoli/Argentina",
    position: "Forward",
    jerseyNumber: 10,
    yearsActive: "1976-1997",
    yearStart: 1976,
    yearEnd: 1997,
    country: "Argentina",
    isRetired: true,
  },
  {
    id: "zidane",
    firstName: "Zinedine",
    lastName: "Zidane",
    fullName: "Zinedine Zidane",
    sport: "soccer",
    league: "Various",
    team: "Real Madrid/France",
    position: "Midfielder",
    jerseyNumber: 5,
    yearsActive: "1989-2006",
    yearStart: 1989,
    yearEnd: 2006,
    country: "France",
    isRetired: true,
  },
  {
    id: "henry",
    firstName: "Thierry",
    lastName: "Henry",
    fullName: "Thierry Henry",
    sport: "soccer",
    league: "Premier League",
    team: "Arsenal",
    position: "Forward",
    jerseyNumber: 14,
    yearsActive: "1994-2014",
    yearStart: 1994,
    yearEnd: 2014,
    country: "France",
    isRetired: true,
  },
  {
    id: "ronaldinho",
    firstName: "Ronaldinho",
    lastName: "",
    fullName: "Ronaldinho",
    sport: "soccer",
    league: "Various",
    team: "Barcelona/Brazil",
    position: "Forward",
    jerseyNumber: 10,
    yearsActive: "1998-2015",
    yearStart: 1998,
    yearEnd: 2015,
    country: "Brazil",
    isRetired: true,
  },
  {
    id: "beckham",
    firstName: "David",
    lastName: "Beckham",
    fullName: "David Beckham",
    sport: "soccer",
    league: "Premier League",
    team: "Manchester United",
    position: "Midfielder",
    jerseyNumber: 7,
    yearsActive: "1992-2013",
    yearStart: 1992,
    yearEnd: 2013,
    country: "England",
    isRetired: true,
  },
  {
    id: "xavi",
    firstName: "Xavi",
    lastName: "Hernandez",
    fullName: "Xavi Hernandez",
    sport: "soccer",
    league: "La Liga",
    team: "Barcelona",
    position: "Midfielder",
    jerseyNumber: 6,
    yearsActive: "1998-2019",
    yearStart: 1998,
    yearEnd: 2019,
    country: "Spain",
    isRetired: true,
  },
  {
    id: "iniesta",
    firstName: "Andres",
    lastName: "Iniesta",
    fullName: "Andres Iniesta",
    sport: "soccer",
    league: "La Liga",
    team: "Barcelona",
    position: "Midfielder",
    jerseyNumber: 8,
    yearsActive: "2002-present",
    yearStart: 2002,
    yearEnd: null,
    country: "Spain",
    isRetired: false,
  },
]

export const allPlayers = [...nbaPlayers, ...soccerPlayers]

// Helper functions

// Helper to parse height to inches
export function heightToInches(height: string): number {
  const match = height.match(/(\d+)'(\d+)"?/)
  if (!match) return 0
  return parseInt(match[1]) * 12 + parseInt(match[2])
}

// Check if years overlap
export function yearsOverlap(player1: Player, player2: Player): boolean {
  const p1End = player1.yearEnd || new Date().getFullYear()
  const p2End = player2.yearEnd || new Date().getFullYear()
  return player1.yearStart <= p2End && p1End >= player2.yearStart
}

// Get all unique player names for autocomplete
export function getAllPlayerNames(): string[] {
  return allPlayers.map((p) => p.fullName).sort()
}
